/**
 * Credit Report React Query hooks
 * Uses Gobodo Graph Traversal API (Client INCOMING → CreditReport) when enabled.
 * Falls back to getRecordsForObject + client filter on traversal failure or non-numeric clientId.
 * When USE_GOBODO_API is false, uses creditReportService.getClientCRRecords.
 *
 * PE-1576: File storage (File__c, S3) integration will extend this for PDF attachments.
 */

import { useQueries, useQuery } from '@tanstack/react-query';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { getCreditReportClientRelKey } from '../../config/gobodoAccountRelationshipKeys';
import { getRecordById, getRecordsForObject } from '../recordStorageService';
import { convertFromGobodoRecord, getGobodoRecordCount } from '../gobodo/gobodoRecordService';
import { findGraphUserBySfId, looksLikeGraphUserSfId } from '../gobodo/gobodoUserSfIdLookup';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { creditReportService } from '../creditReportService';
import type { CreditReportRecord } from '../../types';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const CREDIT_REPORT_OBJECT = `CreditReport${CUSTOM_SUFFIX}`;
const CLIENT_OBJECT = `Client${CUSTOM_SUFFIX}`;
const CREDIT_REPORT_CACHE_DURATION_SECONDS = 90;

const clientIdFields = [
  `ClientRelField${CUSTOM_SUFFIX}`,
  'ClientRelField__c',
  'Client__c',
  'AccountRelField__c',
  'AccountId__c',
  'accountId',
  'custom__client',
];

const getClientId = (r: Record<string, unknown>): string => {
  for (const f of clientIdFields) {
    const v = r[f];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
};

const matchesClient = (recordClientId: string, clientId: string): boolean => {
  if (!recordClientId || !clientId) return false;
  if (recordClientId === clientId) return true;
  const stripSuffix = (s: string) => s.replace(/__stg$|__c$|__dev_c$/i, '');
  return stripSuffix(recordClientId) === stripSuffix(clientId);
};

const normalizeBureau = (val: unknown): CreditReportRecord['bureau'] => {
  const s = String(val ?? '').toLowerCase();
  if (s.includes('experian')) return 'experian';
  if (s.includes('equifax')) return 'equifax';
  if (s.includes('transunion') || s.includes('trans union')) return 'transunion';
  if (s.includes('tri') || s.includes('merge')) return 'tri_merge';
  return 'experian';
};

const extractClientName = (r: Record<string, unknown>): string =>
  String(r.ClientName__c ?? r.clientName ?? r.AccountName__c ?? r.accountName ?? '').trim();

const extractCreatedBy = (r: Record<string, unknown>): string =>
  String(
    r.created_by_name__stg3_c ??
      r.created_by_name ??
      r.CreatedByName__c ??
      r.CreatedBy__c ??
      r.CreatedById__c ??
      r.createdBy ??
      ''
  ).trim();

const looksResolvedDisplayName = (value: string): boolean =>
  !!value.trim() && !looksLikeGraphUserSfId(value.trim());

const normalizeQueryKeyToken = (value: string): string => value.replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '') || value;

function extractRawObjectsAndTotal(rawResponse: unknown): { rawObjects: any[]; totalCount?: number } {
  if (Array.isArray(rawResponse)) return { rawObjects: rawResponse };
  if (!rawResponse || typeof rawResponse !== 'object') return { rawObjects: [] };
  const raw = rawResponse as Record<string, unknown>;
  const rawObjects = Array.isArray(raw.content)
    ? raw.content
    : Array.isArray(raw.items)
      ? raw.items
      : Array.isArray(raw.data)
        ? raw.data
        : [];
  const totalCount =
    typeof raw.totalElements === 'number'
      ? raw.totalElements
      : typeof raw.total === 'number'
        ? raw.total
        : typeof raw.count === 'number'
          ? raw.count
          : undefined;
  return { rawObjects, totalCount };
}

function toRawCreditReportRecord(obj: any): Record<string, unknown> {
  const d = obj?.data || obj?.properties || {};
  return { ...d, _gobodoKey: obj?.key, _gobodoLabel: obj?.label } as Record<string, unknown>;
}

function readField(r: Record<string, unknown>, ...names: string[]): string | undefined {
  for (const n of names) {
    const v = r[n];
    if (v != null && String(v).trim()) return String(v);
  }
  return undefined;
}

/** Build `attachedFiles` from Array file-ID fields. Falls back to empty when
 *  the row is a legacy SF-sourced record without Array metadata. */
function deriveAttachedFiles(r: Record<string, unknown>, orderId: string | undefined): string[] {
  const out: string[] = [];
  const pdfId = readField(r, `ArrayPdfFileId${CUSTOM_SUFFIX}`, 'ArrayPdfFileId__c');
  const jsonId = readField(r, `ArrayJsonFileId${CUSTOM_SUFFIX}`, 'ArrayJsonFileId__c');
  if (pdfId) {
    const name = orderId
      ? `credit-report-array-${orderId}-pdf-protected.pdf`
      : `array-credit-report-${pdfId}.pdf`;
    out.push(name);
  }
  if (jsonId) {
    const name = orderId
      ? `credit-report-array-${orderId}-json.json`
      : `array-credit-report-${jsonId}.json`;
    out.push(name);
  }
  return out;
}

interface RawApprovalEntry {
  id?: string;
  action?: string;
  approvedBy?: string;
  approvalDate?: string;
  notes?: string;
}

/** Parse JSON-stringified Array approval history. */
function deriveApprovalHistory(r: Record<string, unknown>): CreditReportRecord['approvalHistory'] {
  const raw = readField(r, `ArrayApprovalHistory${CUSTOM_SUFFIX}`, 'ArrayApprovalHistory__c');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as RawApprovalEntry[]).map((e, i) => ({
      id: String(e?.id ?? `approval-${i}`),
      approvedBy: String(e?.approvedBy ?? ''),
      approvalDate: String(e?.approvalDate ?? ''),
      action: String(e?.action ?? ''),
      ...(e?.notes != null ? { notes: String(e.notes) } : {}),
    }));
  } catch {
    return [];
  }
}

function mapRawToCreditReportRecord(r: Record<string, unknown>): CreditReportRecord {
  const createdBy = extractCreatedBy(r);
  const clientId = String((r.relatedClientId ?? getClientId(r) ?? '')).trim();
  const clientName = extractClientName(r);
  const arrayOrderId = readField(r, `ArrayOrderId${CUSTOM_SUFFIX}`, 'ArrayOrderId__c');
  const attachedFiles = deriveAttachedFiles(r, arrayOrderId);
  const approvalHistory = deriveApprovalHistory(r);
  return {
    id: String(r._gobodoKey ?? r.key ?? r.sf_id ?? r.Id ?? r.id ?? ''),
    clientId,
    clientName,
    bureau: normalizeBureau(r.CreditRatingCodeType__c ?? r.Bureau__c ?? r.CreditBureau__c ?? r.bureau),
    creditScore: Number(r.score__c ?? r.Score__c) || undefined,
    alias: String(r.Name__c ?? r._gobodoLabel ?? r.name ?? '').trim() || undefined,
    grade: String(r.grade__c ?? r.Grade__c ?? r.grade ?? '').trim() || undefined,
    rating: String(r.Rating__c ?? r.rating ?? '').trim() || undefined,
    pullDate: String(r.Credit_Report_Pull_Date__c ?? r.creditReportPullDate ?? r.pullDate ?? '').slice(0, 10),
    productCode: String(r.Credit_Report_Product_Code__c ?? r.Product_Code__c ?? r.productCode ?? 'CR'),
    requestStatus: readField(r, `ArrayRequestStatus${CUSTOM_SUFFIX}`, 'ArrayRequestStatus__c'),
    reportKey: String(r.Report_Key__c ?? r.CreditReportIdentifier__c ?? r.reportKey ?? ''),
    pdfUrl: String(r.Credit_Report_Url__c ?? r.creditReportUrl ?? r.pdfUrl ?? '').trim() || undefined,
    systemInfo: {
      createdBy,
      status: String(r.Credit_Report_Status__c ?? r.creditReportStatus ?? r.status ?? 'success').toLowerCase().includes('fail')
        ? 'failure'
        : 'success',
      createdDate: String(r.CreatedDate__c ?? r.createdDate ?? ''),
      totalFilesAttached:
        Number(r.Total_Files_Attached__c ?? r.totalFilesAttached ?? 0) || attachedFiles.length,
      attachedFiles,
    },
    relatedReports: [],
    approvalHistory,
  };
}

async function resolveClientForReport(
  reportObjectKey: string,
  fallbackClientId: string
): Promise<{ clientId: string; clientName: string }> {
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectKey(reportObjectKey);
  input.setRelationshipTypes([getCreditReportClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
  input.setEndObjectTypes([CLIENT_OBJECT]);
  input.setMaxPathLength(1);

  const targets = await retryWithBackoff(
    () =>
      traversalApi.traverseObjects(input, {
        cachekey: ['credit-report-row-client', normalizeQueryKeyToken(reportObjectKey)],
        cacheduration: String(CREDIT_REPORT_CACHE_DURATION_SECONDS),
      }),
    { maxRetries: 2, initialDelayMs: 1000 }
  ).catch(() => []);

  if (!Array.isArray(targets) || targets.length === 0) {
    return {
      clientId: fallbackClientId,
      clientName: '',
    };
  }

  const client = targets[0] as Record<string, unknown>;
  const clientData =
    client.data && typeof client.data === 'object'
      ? (client.data as Record<string, unknown>)
      : {};
  return {
    clientId: String(client.key ?? fallbackClientId ?? '').trim(),
    clientName: String(clientData.Name__c ?? client.label ?? '').trim(),
  };
}

const mapToCreditReportRecord = (
  r: Record<string, unknown>,
  clientId: string,
  clientName: string
): CreditReportRecord => {
  const bureau = normalizeBureau(r.CreditBureau__c ?? r.CreditBureau ?? r.bureau);
  const pullDate = String(
    r.Credit_Report_Pull_Date__c ?? r.creditReportPullDate ?? r.pullDate ?? r.createdDate ?? new Date().toISOString()
  ).slice(0, 10);
  const status = String(r.Credit_Report_Status__c ?? r.creditReportStatus ?? r.status ?? 'success').toLowerCase();
  const score = r.score__c ?? r.Score__c ?? r.creditScore;
  const numericScore = typeof score === 'number' ? score : Number(score) || undefined;
  const arrayOrderId = readField(r, `ArrayOrderId${CUSTOM_SUFFIX}`, 'ArrayOrderId__c');
  const attachedFiles = deriveAttachedFiles(r, arrayOrderId);
  const approvalHistory = deriveApprovalHistory(r);
  return {
    id: String(r.id ?? `CR-${Date.now()}`),
    clientId,
    clientName,
    bureau,
    creditScore: numericScore,
    alias: String(r.Name__c ?? r.name ?? r.label ?? '').trim() || undefined,
    grade: String(r.grade__c ?? r.Grade__c ?? r.grade ?? '').trim() || undefined,
    rating: String(r.Rating__c ?? r.rating ?? '').trim() || undefined,
    pullDate,
    productCode: String(r.Credit_Report_Product_Code__c ?? r.productCode ?? r.ProductCode ?? 'CR'),
    reportKey: String(r.CreditReportIdentifier__c ?? r.reportKey ?? r.id ?? ''),
    pdfUrl: String(r.Credit_Report_Url__c ?? r.creditReportUrl ?? r.pdfUrl ?? '').trim() || undefined,
    systemInfo: {
      createdBy: String(r.CreatedBy__c ?? r.createdBy ?? ''),
      status: status.includes('fail') ? 'failure' : 'success',
      createdDate: String(r.CreatedDate__c ?? r.createdDate ?? ''),
      totalFilesAttached:
        Number(r.Total_Files_Attached__c ?? r.totalFilesAttached ?? 0) || attachedFiles.length,
      attachedFiles,
    },
    relatedReports: [],
    approvalHistory,
  };
};

/** Fetch credit report records for one client via traversal (Client INCOMING → CreditReport). clientId is the client's object key. */
async function getCreditReportsViaTraversal(
  clientId: string,
  clientName: string
): Promise<CreditReportRecord[]> {
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectKey(clientId);
  input.setRelationshipTypes([getCreditReportClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([CREDIT_REPORT_OBJECT]);
  input.setMaxPathLength(1);

  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  if (!result || !Array.isArray(result)) return [];

  // Gobodo's traverseObjects often returns sparse property payloads
  // (id/key/label only on each node). For Array CR records that means
  // ArrayPdfFileId__c, Credit_Report_Pull_Date__c, etc. are absent from
  // the traversal response even though they exist on the record. Re-fetch
  // each row by id to pull full property data, mirroring the
  // csAuditHydration pattern at services/queries/csAuditHydration.ts:49.
  const HYDRATE_CONCURRENCY = 8;
  const ids: string[] = result.map((obj: any) => String(obj?.id ?? '')).filter(Boolean);
  const hydratedById = new Map<string, Record<string, unknown>>();
  for (let i = 0; i < ids.length; i += HYDRATE_CONCURRENCY) {
    const slice = ids.slice(i, i + HYDRATE_CONCURRENCY);
    const chunk = await Promise.all(
      slice.map(async (id) => {
        try {
          const full = await getRecordById(CREDIT_REPORT_OBJECT, id);
          return full ? ([id, full as Record<string, unknown>] as const) : null;
        } catch {
          return null;
        }
      }),
    );
    for (const entry of chunk) {
      if (entry) hydratedById.set(entry[0], entry[1]);
    }
  }

  const records: CreditReportRecord[] = [];
  for (const gobodoObj of result) {
    try {
      const sparse = convertFromGobodoRecord(gobodoObj, CREDIT_REPORT_OBJECT) as Record<string, unknown>;
      const id = String(gobodoObj?.id ?? '');
      const full = hydratedById.get(id);
      // Merge: full record's fields take precedence, but sparse keeps
      // _gobodoKey / _gobodoLabel / relatedClientId hints from traversal.
      const merged = full ? { ...sparse, ...full } : sparse;
      records.push(mapToCreditReportRecord(merged, clientId, clientName));
    } catch {
      // skip malformed record
    }
  }
  return records;
}

/** Fallback: fetch all CreditReport records and filter by clientId. */
async function getCreditReportsViaFetchAndFilter(
  clientId: string,
  clientName: string
): Promise<CreditReportRecord[]> {
  const records = await getRecordsForObject(CREDIT_REPORT_OBJECT);
  const forClient = (records as Record<string, unknown>[]).filter((r) =>
    matchesClient(getClientId(r), clientId)
  );
  return forClient.map((r) => mapToCreditReportRecord(r, clientId, clientName));
}

export function useCreditReportRecords(clientId: string, clientName: string, enabled = true) {
  const useGobodo = USE_GOBODO_API;

  return useQuery({
    queryKey: ['credit-report-records', clientId],
    queryFn: async (): Promise<CreditReportRecord[]> => {
      if (!useGobodo) {
        return creditReportService.getClientCRRecords(clientId);
      }
      try {
        return await getCreditReportsViaTraversal(clientId, clientName);
      } catch {
        return getCreditReportsViaFetchAndFilter(clientId, clientName);
      }
    },
    enabled: !!clientId && enabled,
    staleTime: 60_000,
  });
}

export interface CreditReportsPaginatedResult {
  reports: CreditReportRecord[];
  totalCount: number;
}

export interface CreditReportRowHydration {
  clientId?: string;
  clientName?: string;
  createdBy?: string;
}

export function useCreditReportsPaginated(page: number, pageSize: number, enabled = true) {
  const useGobodo = USE_GOBODO_API;

  return useQuery({
    queryKey: ['credit-reports', 'paginated', page, pageSize, CUSTOM_SUFFIX],
    queryFn: async (): Promise<CreditReportsPaginatedResult> => {
      if (!useGobodo) {
        const allReports = creditReportService.getAllCRRecords();
        const start = Math.max(0, (page - 1) * pageSize);
        return {
          reports: allReports.slice(start, start + pageSize),
          totalCount: allReports.length,
        };
      }

      const apiClient = getGobodoClient();
      const objApi = new GobodoApi.GraphObjectsApi(apiClient);
      const graphId = GobodoConfig.graph();
      const pageIndex = Math.max(0, page - 1);

      const [rawResponse, totalCountFallback] = await Promise.all([
        retryWithBackoff(
          () =>
            objApi.getObjectsInGraphByType(graphId, CREDIT_REPORT_OBJECT, {
              page: pageIndex,
              size: pageSize,
              cachekey: ['credit-reports-page', String(pageIndex), String(pageSize)],
              cacheduration: String(CREDIT_REPORT_CACHE_DURATION_SECONDS),
            }),
          { maxRetries: 2, initialDelayMs: 1000 }
        ),
        getGobodoRecordCount(CREDIT_REPORT_OBJECT).catch(() => 0),
      ]);

      const { rawObjects, totalCount } = extractRawObjectsAndTotal(rawResponse);
      const reports = rawObjects.map((obj) => mapRawToCreditReportRecord(toRawCreditReportRecord(obj)));
      return {
        reports,
        totalCount: totalCount ?? totalCountFallback ?? reports.length,
      };
    },
    enabled: page >= 1 && pageSize >= 1 && enabled,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  });
}

export function useCreditReportHydrationQueries(reports: CreditReportRecord[], enabled = true) {
  return useQueries({
    queries: reports.map((report, rowIndex) => {
      const reportKey = String(report.id ?? '').trim();
      const createdByToken = String(report.systemInfo?.createdBy ?? '').trim();
      const needsCreatedBy = !!createdByToken && !looksResolvedDisplayName(createdByToken);
      const needsClient = !String(report.clientName ?? '').trim() || !String(report.clientId ?? '').trim();
      const hydrationKey = reportKey || `row-${rowIndex}`;
      return {
        queryKey: [
          'credit-reports',
          'row-hydration',
          normalizeQueryKeyToken(hydrationKey),
          normalizeQueryKeyToken(createdByToken || 'no-created-by'),
          normalizeQueryKeyToken(String(report.clientId ?? '') || 'no-client'),
          CUSTOM_SUFFIX,
        ] as const,
        queryFn: async (): Promise<CreditReportRowHydration> => {
          const [clientHydration, createdByHydration] = await Promise.all([
            needsClient && reportKey
              ? resolveClientForReport(reportKey, String(report.clientId ?? '').trim())
              : Promise.resolve({
                  clientId: String(report.clientId ?? '').trim(),
                  clientName: String(report.clientName ?? '').trim(),
                }),
            needsCreatedBy
              ? findGraphUserBySfId(createdByToken).then((user) => user?.displayName ?? '')
              : Promise.resolve(createdByToken),
          ]);

          return {
            clientId: clientHydration.clientId || String(report.clientId ?? '').trim() || undefined,
            clientName: clientHydration.clientName || String(report.clientName ?? '').trim() || undefined,
            createdBy: createdByHydration || createdByToken || undefined,
          };
        },
        enabled: USE_GOBODO_API && enabled && (!!reportKey || !!createdByToken) && (needsClient || needsCreatedBy),
        staleTime: 60_000,
      };
    }),
  });
}

/** Fetch ALL credit report records across all clients (for the Reports tab list view).
 *  Reads raw Gobodo data to preserve __c field names and avoid creatorid override. */
export function useAllCreditReports(enabled = true) {
  return useQuery({
    queryKey: ['credit-reports', 'all'],
    queryFn: async (): Promise<CreditReportRecord[]> => {
      // Fetch raw Gobodo objects (not via convertFromGobodoRecord which loses __c fields)
      const apiClient = getGobodoClient();
      const objApi = new GobodoApi.GraphObjectsApi(apiClient);
      const graphId = GobodoConfig.graph();
      const rawObjects = await retryWithBackoff(
        () => objApi.getObjectsInGraphByType(graphId, CREDIT_REPORT_OBJECT),
        { maxRetries: 2, initialDelayMs: 1000 }
      );
      if (!rawObjects || !Array.isArray(rawObjects)) return [];

      // Extract data from raw Gobodo objects
      const recs = rawObjects.map((obj: any) => {
        const d = obj.data || obj.properties || {};
        return { ...d, _gobodoKey: obj.key, _gobodoLabel: obj.label } as Record<string, unknown>;
      });

      // Collect SF user IDs for batch name resolution
      const userIds: string[] = [];
      const seenUsers = new Set<string>();
      for (const r of recs) {
        const uid = String(r.CreatedById__c ?? '').trim();
        if (uid.startsWith('005') && !seenUsers.has(uid)) { userIds.push(uid); seenUsers.add(uid); }
      }

      // Batch resolve user names
      let userNameMap = new Map<string, string>();
      if (userIds.length > 0) {
        try {
          const { resolveGraphUserNamesBySfIds } = await import('../gobodo/gobodoUserSfIdLookup');
          userNameMap = await resolveGraphUserNamesBySfIds(userIds);
        } catch {}
      }

      // Resolve client names via traversal (credit_report → client relationship)
      const clientNameMap = new Map<string, string>(); // crSfId → clientName
      const clientIdMap = new Map<string, string>();   // crSfId → clientGobodoKey
      try {
        const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
        const relKey = getCreditReportClientRelKey();
        for (const obj of rawObjects) {
          const crKey = obj.key;
          if (!crKey) continue;
          try {
            const input = new GobodoApi.GraphTraversalInput();
            input.setGraphId(GobodoConfig.graph());
            input.setStartObjectKey(crKey);
            input.setRelationshipTypes([relKey]);
            input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
            input.setMaxPathLength(1);
            const targets = await traversalApi.traverseObjects(input);
            if (Array.isArray(targets) && targets.length > 0) {
              const client = targets[0];
              const clientData = client.data || {};
              const name = String(clientData.Name__c || client.label || '');
              const sfId = String(client.key || '').replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');
              if (name) clientNameMap.set(crKey, name);
              if (sfId) clientIdMap.set(crKey, sfId);
            }
          } catch {}
        }
      } catch {}

      return recs.map((r, idx) => {
        const crKey = rawObjects[idx]?.key || '';
        const clientName = clientNameMap.get(crKey) || '';
        const rawClientId = clientIdMap.get(crKey) || '';
        const createdBySfId = String(r.CreatedById__c ?? '').trim();
        const createdByName = userNameMap.get(createdBySfId) || '';

        return {
          id: String(r._gobodoKey ?? r.sf_id ?? r.Id ?? ''),
          clientId: rawClientId,
          clientName,
          bureau: normalizeBureau(r.CreditRatingCodeType__c ?? r.Bureau__c ?? r.CreditBureau__c),
          creditScore: Number(r.score__c ?? r.Score__c) || undefined,
          alias: String(r.Name__c ?? r._gobodoLabel ?? ''),
          grade: String(r.grade__c ?? '').trim() || undefined,
          rating: String(r.Rating__c ?? '').trim() || undefined,
          pullDate: String(r.Credit_Report_Pull_Date__c ?? '').slice(0, 10),
          productCode: String(r.Product_Code__c ?? 'CR'),
          reportKey: String(r.Report_Key__c ?? r.CreditReportIdentifier__c ?? ''),
          pdfUrl: String(r.Credit_Report_Url__c ?? '').trim() || undefined,
          systemInfo: {
            createdBy: createdByName,
            status: String(r.Credit_Report_Status__c ?? 'success').toLowerCase().includes('fail') ? 'failure' : 'success',
            createdDate: String(r.CreatedDate__c ?? ''),
            totalFilesAttached: Number(r.Total_Files_Attached__c ?? 0),
            attachedFiles: [],
          },
          relatedReports: [],
          approvalHistory: [],
        } as CreditReportRecord;
      });
    },
    enabled: USE_GOBODO_API && enabled,
    staleTime: 60_000,
  });
}
